import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Save, Building2, User, Target, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/common/Button';
import { Label } from '@/components/common/Label';
import { useToast } from '@/components/common/Toast';
import { PageHeader } from '@/components/common/PageHeader';
import { mqlService, MQL } from '@/services/api/mqlService';
import { accountsService } from '@/services/accounts.service';
import { contactsService } from '@/services/contacts.service';

interface AccountItem { id: string; name: string; }
interface ContactItem { id: string; name: string; accountId?: string; account_id?: string; }

export default function MQLEditPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { addToast } = useToast();

  const [mql, setMql] = useState<MQL | null>(null);
  const [accounts, setAccounts] = useState<AccountItem[]>([]);
  const [contacts, setContacts] = useState<ContactItem[]>([]);
  const [filteredContacts, setFilteredContacts] = useState<ContactItem[]>([]);
  const [loadingPage, setLoadingPage] = useState(true);
  const [loadingData, setLoadingData] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    accountId: '',
    accountName: '',
    contactId: '',
    contactName: '',
    score: 70,
    budgetChecked: false,
    authorityChecked: false,
    needChecked: false,
    timelineChecked: false,
    notes: '',
  });

  // Load MQL from API
  useEffect(() => {
    if (!id) return;
    const load = async () => {
      try {
        setLoadingPage(true);
        const data = await mqlService.getMQLById(id);
        setMql(data);
        setFormData({
          accountId: data.accountId || '',
          accountName: data.accountName,
          contactId: data.contactId || '',
          contactName: data.contactName,
          score: data.score,
          budgetChecked: data.criteria.budget,
          authorityChecked: data.criteria.authority,
          needChecked: data.criteria.need,
          timelineChecked: data.criteria.timeline,
          notes: data.notes,
        });
      } catch (error: any) {
        addToast({ type: 'error', title: 'MQL Not Found', description: error.message });
        navigate('/mql');
      } finally {
        setLoadingPage(false);
      }
    };
    load();
  }, [id]);

  // Load accounts and contacts
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoadingData(true);
        const [accResp, ctcResp] = await Promise.all([
          accountsService.getAccounts({ limit: 1000 }),
          contactsService.getContacts({ limit: 1000 }),
        ]);
        setAccounts(accResp.data);
        const transformed = ctcResp.data.map((c: any) => ({
          ...c,
          name: c.name || `${c.firstName} ${c.lastName}`,
          accountId: c.accountId || c.account_id,
        }));
        setContacts(transformed);
      } catch (err) {
        console.error('Error loading accounts/contacts:', err);
      } finally {
        setLoadingData(false);
      }
    };
    loadData();
  }, []);

  // Filter contacts by account
  useEffect(() => {
    if (formData.accountId) {
      setFilteredContacts(contacts.filter(c => c.accountId === formData.accountId || c.account_id === formData.accountId));
    } else {
      setFilteredContacts([]);
    }
  }, [formData.accountId, contacts]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!mql) return;
    try {
      setSubmitting(true);
      await mqlService.updateMQL(mql.id, {
        accountId: formData.accountId,
        contactId: formData.contactId || undefined,
        contactName: formData.contactName,
        score: formData.score,
        notes: formData.notes,
        criteria: {
          budget: formData.budgetChecked,
          authority: formData.authorityChecked,
          need: formData.needChecked,
          timeline: formData.timelineChecked,
        },
      });
      addToast({ type: 'success', title: 'MQL Updated', description: 'Marketing Qualified Lead updated successfully.' });
      navigate(`/mql/${mql.id}`);
    } catch (error: any) {
      addToast({ type: 'error', title: 'Update Failed', description: error.message });
    } finally {
      setSubmitting(false);
    }
  };

  const bantCount = [formData.budgetChecked, formData.authorityChecked, formData.needChecked, formData.timelineChecked].filter(Boolean).length;

  if (loadingPage) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading MQL...</p>
        </div>
      </div>
    );
  }

  if (!mql) return null;

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <PageHeader
        onBack={() => navigate(`/mql/${mql.id}`)}
        title="Edit MQL"
        description={`Update Marketing Qualified Lead — ${mql.accountName}`}
      />

      <form onSubmit={handleSubmit}>
        <div className="bg-card border rounded-lg shadow-sm">
          {/* Account & Contact */}
          <div className="p-6 border-b space-y-6">
            <div className="flex items-center gap-2 text-lg font-semibold">
              <Building2 className="h-5 w-5 text-primary" /><span>Account & Contact</span>
            </div>
            {loadingData && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary" />
                <span>Loading accounts and contacts...</span>
              </div>
            )}
            <div className="grid gap-6 md:grid-cols-2">
              <div className="space-y-2">
                <Label className="flex items-center gap-2"><Building2 className="h-4 w-4" />Account Name <span className="text-destructive">*</span></Label>
                <select value={formData.accountId}
                  onChange={(e) => {
                    const acc = accounts.find(a => a.id === e.target.value);
                    setFormData({ ...formData, accountId: e.target.value, accountName: acc?.name || '', contactId: '', contactName: '' });
                  }}
                  className="w-full px-4 py-2.5 border border-input rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring" required>
                  <option value="">Select Account...</option>
                  {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
                <p className="text-xs text-muted-foreground"><CheckCircle2 className="h-3 w-3 inline mr-1" />{accounts.length} accounts available</p>
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-2"><User className="h-4 w-4" />Contact Name</Label>
                <select value={formData.contactId}
                  onChange={(e) => {
                    const c = filteredContacts.find(c => c.id === e.target.value);
                    setFormData({ ...formData, contactId: e.target.value, contactName: c?.name || '' });
                  }}
                  disabled={!formData.accountId}
                  className="w-full px-4 py-2.5 border border-input rounded-md bg-background disabled:bg-muted disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-ring">
                  <option value="">Select Contact...</option>
                  {filteredContacts.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <p className="text-xs text-muted-foreground">
                  {!formData.accountId ? '→ Select account first' : filteredContacts.length === 0 ? '⚠️ No contacts for this account' : `${filteredContacts.length} contacts available`}
                </p>
              </div>
            </div>
          </div>

          {/* Lead Score */}
          <div className="p-6 border-b space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-lg font-semibold">
                <Target className="h-5 w-5 text-primary" /><span>Lead Score</span>
              </div>
              <div className={`px-4 py-2 rounded-full border-2 font-bold text-2xl ${formData.score >= 80 ? 'bg-green-50 border-green-200 text-green-600' : formData.score >= 60 ? 'bg-blue-50 border-blue-200 text-blue-600' : formData.score >= 40 ? 'bg-yellow-50 border-yellow-200 text-yellow-600' : 'bg-red-50 border-red-200 text-red-600'}`}>
                {formData.score}
              </div>
            </div>
            <input type="range" min="0" max="100" value={formData.score}
              onChange={(e) => setFormData({ ...formData, score: parseInt(e.target.value) })}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary" />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span className="text-red-600">0 – Cold</span>
              <span className="text-yellow-600">50 – Warm</span>
              <span className="text-green-600">100 – Hot</span>
            </div>
          </div>

          {/* BANT */}
          <div className="p-6 border-b space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-lg font-semibold">
                <CheckCircle2 className="h-5 w-5 text-primary" /><span>BANT Criteria</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <span className="font-medium">{bantCount}/4 Complete</span>
                <div className="flex gap-1">
                  {[0, 1, 2, 3].map(i => (
                    <div key={i} className={`h-2 w-8 rounded-full ${i < bantCount ? 'bg-green-500' : 'bg-gray-200'}`} />
                  ))}
                </div>
              </div>
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              {[
                { key: 'budgetChecked', label: 'Budget', desc: 'Has allocated budget' },
                { key: 'authorityChecked', label: 'Authority', desc: 'Has decision-making authority' },
                { key: 'needChecked', label: 'Need', desc: 'Clear business need identified' },
                { key: 'timelineChecked', label: 'Timeline', desc: 'Has defined timeline' },
              ].map(({ key, label, desc }) => (
                <label key={key} className="flex items-start gap-3 p-4 border rounded-lg cursor-pointer hover:bg-accent/50 transition-colors">
                  <input type="checkbox"
                    checked={formData[key as keyof typeof formData] as boolean}
                    onChange={(e) => setFormData({ ...formData, [key]: e.target.checked })}
                    className="mt-0.5 h-5 w-5 rounded border-gray-300 text-primary cursor-pointer" />
                  <div>
                    <div className="font-medium">{label}</div>
                    <div className="text-xs text-muted-foreground">{desc}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div className="p-6 space-y-4">
            <Label className="text-lg font-semibold">Additional Notes</Label>
            <textarea value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={4} placeholder="Add any additional information about this lead..."
              className="w-full px-4 py-3 border border-input rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring resize-none" />
          </div>

          {/* Actions */}
          <div className="p-6 bg-muted/30 rounded-b-lg flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={() => navigate(`/mql/${mql.id}`)}>Cancel</Button>
            <Button type="submit" className="gap-2" disabled={!formData.accountId || submitting}>
              <Save className="h-4 w-4" />{submitting ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}
